import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  useTheme,
  useMediaQuery,
  Divider,
  Menu,
  MenuItem,
  Collapse
} from '@mui/material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Menu as MenuIcon,
  Logout as LogoutIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Receipt as ReceiptIcon,
  BeachAccess as VacacionesIcon,
  Work as WorkIcon,
  Error as ErrorIcon,
  Security as SecurityIcon,
  MonitorHeart as MonitorIcon,
  Home as HomeIcon,
  Assignment as AssignmentIcon,
  AccountBalance as CMOIcon,
  Email as EmailIcon,
  Settings as SettingsIcon,
  ExpandLess,
  ExpandMore
} from '@mui/icons-material';

import { getUser, clearAuthData } from '../utils/auth';
import usePermisos from '../hooks/usePermisos';
import NotificationBell from './NotificationBell';

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = getUser();
  const { tienePermiso, esSuperAdmin, loading } = usePermisos();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cmoMenuAnchor, setCmoMenuAnchor] = useState(null);
  const [cmoSubmenuOpen, setCmoSubmenuOpen] = useState(false);
  const [configMenuAnchor, setConfigMenuAnchor] = useState(null);
  const [configSubmenuOpen, setConfigSubmenuOpen] = useState(false);

  // Verificar si está en el flujo de preguntas iniciales
  const enPreguntasIniciales = location.pathname === '/preguntas-iniciales';

  const handleLogout = () => {
    clearAuthData();
    navigate('/login');
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const getInitials = (user) => {
    if (!user) return '';
    const nombre = user.nombre || user.nombreCompleto || user.displayName || '';
    const apellido = user.apellido || '';
    const first = nombre ? nombre.trim().split(' ')[0].charAt(0).toUpperCase() : '';
    const last = apellido ? apellido.trim().split(' ')[0].charAt(0).toUpperCase() : '';
    return `${first}${last}` || (nombre ? nombre.trim().split(' ')[0].charAt(0).toUpperCase() : '');
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const handleCmoMenuOpen = (event) => {
    setCmoMenuAnchor(event.currentTarget);
  };

  const handleCmoMenuClose = () => {
    setCmoMenuAnchor(null);
  };

  const handleConfigMenuOpen = (event) => {
    setConfigMenuAnchor(event.currentTarget);
  };

  const handleConfigMenuClose = () => {
    setConfigMenuAnchor(null);
  };

  // Definir los elementos del menú
  const getMenuItems = () => {
    const items = [];

    if (!enPreguntasIniciales) {
      // Inicio/Dashboard según el rol
      if (user.rol === 'empleado') {
        items.push({
          text: 'INICIO',
          path: '/bienvenida',
          icon: <HomeIcon />,
          show: true
        });
      } else if (tienePermiso('dashboard', 'ver')) {
        items.push({
          text: 'DASHBOARD',
          path: '/dashboard',
          icon: <DashboardIcon />,
          show: true
        });
      }

      // Usuarios
      if (tienePermiso('usuarios', 'ver')) {
        items.push({
          text: 'USUARIOS',
          path: '/usuarios',
          icon: <PeopleIcon />,
          show: true
        });
      }

      // Legajo para empleados
      if (tienePermiso('legajo', 'ver_propio')) {
        items.push({
          id: 'legajo-empleado',
          text: 'LEGAJO',
          path: '/legajos',
          icon: <AssignmentIcon />,
          show: true
        });
      }

      // Legajos para admin
      if (tienePermiso('legajos', 'ver_todos')) {
        items.push({
          id: 'legajos-admin',
          text: 'LEGAJOS',
          path: '/legajos',
          icon: <AssignmentIcon />,
          show: true
        });
      }

      // Recibos
      if (tienePermiso('recibos', 'ver_propios') || tienePermiso('recibos', 'ver')) {
        items.push({
          text: 'RECIBOS',
          path: '/recibos',
          icon: <ReceiptIcon />,
          show: true
        });
      }

      // Vacaciones
      if (tienePermiso('vacaciones', 'ver_propias') || tienePermiso('vacaciones', 'ver')) {
        items.push({
          text: 'VACACIONES',
          path: '/vacaciones',
          icon: <VacacionesIcon />,
          show: true
        });
      }

      // Mi Equipo
      if (tienePermiso('usuarios', 'ver_equipo') || tienePermiso('legajos', 'ver_equipo') || tienePermiso('recibos', 'ver_equipo')) {
        items.push({
          text: 'MI EQUIPO',
          path: '/mi-equipo',
          icon: <WorkIcon />,
          show: true
        });
      }

      // CMO - Costo de Mano de Obra
      if (user?.rol && ['admin_rrhh', 'superadmin'].includes(user.rol) && tienePermiso('cmo', 'ver')) {
        items.push({
          text: 'CMO',
          icon: <CMOIcon />,
          show: true,
          hasSubmenu: true,
          submenu: [
            { text: 'Importaciones', path: '/cmo' },
            { text: 'Centros de Costo', path: '/cmo/centros' },
            { text: 'Adicionales', path: '/cmo/adicionales' },
            { text: 'Dashboard Ausentismo', path: '/cmo/ausentismo' }
          ]
        });
      }

      // Solo superadmin
      if (esSuperAdmin()) {
        items.push({
          text: 'CONFIGURACIÓN',
          icon: <SettingsIcon />,
          show: true,
          hasSubmenu: true,
          submenu: [
            { text: 'Errores', path: '/errores' },
            { text: 'Permisos', path: '/permisos' },
            { text: 'Nuevos Permisos', path: '/permisos/nuevos' },
            { text: 'Monitoreo', path: '/monitoring' },
            { text: 'Emails Empresas', path: '/emails-empresas' }
          ]
        });
      }
    }

    return items.filter(item => item.show);
  };

  if (!user || loading) return null;

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: '#1976d2',
          height: '70px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <Toolbar
          sx={{
            minHeight: '70px',
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          {/* Sección izquierda: Logo y menú hamburguesa en móvil */}
          <Box sx={{ display: 'flex', alignItems: 'center', pl: isMobile ? 1 : 2 }}>
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="menu"
                onClick={toggleMobileMenu}
                sx={{ mr: 1 }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Typography 
              variant={isMobile ? "subtitle1" : "h6"} 
              color="inherit"
              sx={{ 
                fontWeight: 'bold',
                fontSize: isMobile ? '1rem' : '1.25rem'
              }}
            >
              Sistema RRHH
            </Typography>
          </Box>

          {/* Sección central: Menú en desktop */}
          {!isMobile && !enPreguntasIniciales && (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              {getMenuItems().map((item) => {
                if (item.hasSubmenu) {
                  const isCmoMenu = item.text === 'CMO';
                  const isConfigMenu = item.text === 'CONFIGURACIÓN';
                  const menuAnchor = isCmoMenu ? cmoMenuAnchor : isConfigMenu ? configMenuAnchor : null;
                  const handleOpen = isCmoMenu ? handleCmoMenuOpen : isConfigMenu ? handleConfigMenuOpen : null;
                  const handleClose = isCmoMenu ? handleCmoMenuClose : isConfigMenu ? handleConfigMenuClose : null;

                  return (
                    <React.Fragment key={item.text}>
                      <Button
                        onClick={handleOpen}
                        sx={{
                          color: 'white',
                          height: '100%',
                          borderRadius: 0,
                          px: 2,
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          '&:hover': {
                            backgroundColor: '#1565c0'
                          }
                        }}
                      >
                        {item.text}
                      </Button>
                      <Menu
                        anchorEl={menuAnchor}
                        open={Boolean(menuAnchor)}
                        onClose={handleClose}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'left',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'left',
                        }}
                      >
                        {item.submenu.map((subitem) => (
                          <MenuItem
                            key={subitem.path}
                            component={Link}
                            to={subitem.path}
                            onClick={handleClose}
                          >
                            {subitem.text}
                          </MenuItem>
                        ))}
                      </Menu>
                    </React.Fragment>
                  );
                } else {
                  return (
                    <Button
                      key={item.id || item.path}
                      component={Link}
                      to={item.path}
                      sx={{
                        color: 'white',
                        height: '100%',
                        borderRadius: 0,
                        px: 2,
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        '&:hover': {
                          backgroundColor: '#1565c0'
                        }
                      }}
                    >
                      {item.text}
                    </Button>
                  );
                }
              })}
            </Box>
          )}

          {/* Mensaje informativo cuando está en preguntas iniciales */}
          {enPreguntasIniciales && !isMobile && (
            <Box sx={{ color: 'white', textAlign: 'center', flex: 1 }}>
              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                Completá las preguntas de seguridad para continuar
              </Typography>
            </Box>
          )}

          {/* Sección derecha: Notificaciones, Usuario y logout */}
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            {/* Campana de notificaciones */}
            {!enPreguntasIniciales && <NotificationBell />}
            
            {/* Info del usuario */}
            <Box 
              sx={{ 
                backgroundColor: '#015f92',
                height: '70px',
                display: 'flex', 
                alignItems: 'center', 
                color: 'white',
                px: isMobile ? 1 : 2,
                minWidth: isMobile ? 'auto' : '200px'
              }}
            >
              <Box sx={{ bgcolor: '#e8f4ff', color: '#1976d2', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: isMobile ? 0.5 : 1 }}>
                <Typography sx={{ color: '#1976d2', fontWeight: 700 }}>{getInitials(user)}</Typography>
              </Box>
              <Typography 
                variant="body2"
                sx={{ 
                  fontSize: isMobile ? '0.75rem' : '0.875rem',
                  maxWidth: isMobile ? '100px' : 'none',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {isMobile ? (user.displayName || user.nombreCompleto).split(' ')[0] : (user.displayName || user.nombreCompleto)}
              </Typography>
            </Box>

            {/* Botón cerrar sesión */}
            <Box
              sx={{
                backgroundColor: 'red',
                height: '70px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Button
                startIcon={!isMobile ? <LogoutIcon sx={{ color: 'white' }} /> : null}
                sx={{
                  color: 'white',
                  height: '100%',
                  borderRadius: 0,
                  px: isMobile ? 1 : 2,
                  minWidth: 'auto',
                  fontSize: isMobile ? '0.75rem' : '0.875rem',
                  '&:hover': {
                    backgroundColor: '#b30000',
                  },
                }}
                onClick={handleLogout}
              >
                {isMobile ? <LogoutIcon /> : 'CERRAR SESIÓN'}
              </Button>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Drawer para menú móvil */}
          <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={closeMobileMenu}
        sx={{
          '& .MuiDrawer-paper': {
            width: 280,
            backgroundColor: '#f5f5f5',
            pt: 2
          }
        }}
      >
          <Box sx={{ px: 2, pb: 2 }}>
          <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
            Sistema RRHH
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {user.displayName || user.nombreCompleto}
          </Typography>
        </Box>
        
        <Divider />
        
        <List>
          {enPreguntasIniciales ? (
            <ListItem>
              <ListItemText 
                primary="Completá las preguntas de seguridad"
                primaryTypographyProps={{ 
                  variant: 'body2', 
                  style: { fontStyle: 'italic' } 
                }}
              />
            </ListItem>
          ) : (
            getMenuItems().map((item) => {
              if (item.hasSubmenu) {
                const isCmoMenu = item.text === 'CMO';
                const isConfigMenu = item.text === 'CONFIGURACIÓN';
                const submenuOpen = isCmoMenu ? cmoSubmenuOpen : isConfigMenu ? configSubmenuOpen : false;
                const toggleSubmenu = isCmoMenu
                  ? () => setCmoSubmenuOpen(!cmoSubmenuOpen)
                  : isConfigMenu
                    ? () => setConfigSubmenuOpen(!configSubmenuOpen)
                    : () => {};

                return (
                  <React.Fragment key={item.text}>
                    <ListItemButton
                      onClick={toggleSubmenu}
                      sx={{
                        py: 1.5,
                        '&:hover': {
                          backgroundColor: '#e3f2fd'
                        }
                      }}
                    >
                      <ListItemIcon sx={{ color: '#1976d2', minWidth: 40 }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{
                          fontWeight: 500,
                          fontSize: '0.875rem'
                        }}
                      />
                      {submenuOpen ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                    <Collapse in={submenuOpen} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {item.submenu.map((subitem) => (
                          <ListItemButton
                            key={subitem.path}
                            component={Link}
                            to={subitem.path}
                            onClick={closeMobileMenu}
                            sx={{
                              pl: 4,
                              py: 1,
                              '&:hover': {
                                backgroundColor: '#e3f2fd'
                              }
                            }}
                          >
                            <ListItemText
                              primary={subitem.text}
                              primaryTypographyProps={{
                                fontSize: '0.875rem'
                              }}
                            />
                          </ListItemButton>
                        ))}
                      </List>
                    </Collapse>
                  </React.Fragment>
                );
              } else {
                return (
                  <ListItemButton
                    key={item.id || item.path}
                    component={Link}
                    to={item.path}
                    onClick={closeMobileMenu}
                    sx={{
                      py: 1.5,
                      '&:hover': {
                        backgroundColor: '#e3f2fd'
                      }
                    }}
                  >
                    <ListItemIcon sx={{ color: '#1976d2', minWidth: 40 }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        fontWeight: 500,
                        fontSize: '0.875rem'
                      }}
                    />
                  </ListItemButton>
                );
              }
            })
          )}
        </List>
      </Drawer>
    </>
  );
};

export default Navbar;
